// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { createContext, useMemo } from 'react';
import { useShellStore } from '../stores/shellStore';
import { useAuth } from '../hooks/useAuth';
import { useActiveSite } from '../hooks/useActiveSite';
import {
  browserTimeZone,
  formatInZone,
  resolveZone,
  resolveZoneId,
  toZonedWallClock,
  type ResolvedZone,
  type TimePreset,
} from '../utils/siteTime';
import { parseInstant } from '../utils/time';

export interface SiteTimeValue extends ResolvedZone {
  /** The site's own zone, undefined on the viewer fallback — plan axes prefer their own offset to that. */
  siteZone?: string;
  /** Render an instant on the active site's clock. */
  format: (
    _value: string | Date | null | undefined,
    _preset: TimePreset,
    _fallback?: string
  ) => string;
  /** The zone a specific site reads on — for rows that span sites. */
  zoneFor: (_siteId: string | null | undefined) => string;
  /** Render an instant on a specific site's clock, for rows that span sites. */
  formatIn: (
    _siteId: string | null | undefined,
    _value: string | Date | null | undefined,
    _preset: TimePreset,
    _fallback?: string
  ) => string;
  /** Wall-clock stand-in for a `@carbon/charts` TIME axis. */
  zonedDate: (_value: string | Date | null | undefined, _timeZone?: string) => Date | undefined;
}

export const SiteTimeContext = createContext<SiteTimeValue | null>(null);

/** Binds `formatInZone` to one zone; shared by the provider and the no-provider default. */
export function bindSiteTime(
  zone: ResolvedZone,
  zoneLookup: (_siteId: string | null | undefined) => string = () => zone.timeZone
): SiteTimeValue {
  return {
    ...zone,
    siteZone: zone.isFallback ? undefined : zone.timeZone,
    format: (value, preset, fallback) => formatInZone(value, preset, zone.timeZone, fallback),
    zoneFor: zoneLookup,
    formatIn: (siteId, value, preset, fallback) =>
      formatInZone(value, preset, zoneLookup(siteId), fallback),
    zonedDate: (value, timeZone) => {
      if (value == null || value === '') return undefined;
      const at = typeof value === 'string' ? parseInstant(value) : value;
      return Number.isNaN(at.getTime())
        ? undefined
        : toZonedWallClock(at, timeZone ?? zone.timeZone);
    },
  };
}

export interface SiteTimeProviderProps {
  children: React.ReactNode;
  /** Test seam: pin the zone instead of resolving it from the active site. */
  timeZoneOverride?: string;
}

/** A pinned zone. Its own component because `ActiveSiteTime` reads `useAuth`, which throws unprovided. */
function PinnedSiteTime({ timeZone, children }: { timeZone: string; children: React.ReactNode }) {
  const value = useMemo(() => bindSiteTime(resolveZone(timeZone)), [timeZone]);
  return <SiteTimeContext.Provider value={value}>{children}</SiteTimeContext.Provider>;
}

function ActiveSiteTime({ children }: { children: React.ReactNode }) {
  const { sites } = useAuth();
  const { siteTimezone } = useActiveSite();
  const policy = useShellStore(s => s.timeZonePolicy);
  const browser = policy === 'browser';

  // Resolved per zone, so the bound `format` identity survives the header's 1 Hz ticks.
  const zone = useMemo(
    () => resolveZone(browser ? browserTimeZone() : siteTimezone),
    [browser, siteTimezone]
  );

  // Resolved once per `sites` change: `zoneFor`/`formatIn` run per row on lists that span sites.
  const zoneById = useMemo(() => {
    const map = new Map<string, string>();
    if (browser) return map;
    for (const site of sites) {
      if (site.site_id) map.set(site.site_id, resolveZoneId(site.timezone));
    }
    return map;
  }, [sites, browser]);

  const value = useMemo(
    () => bindSiteTime(zone, siteId => (siteId && zoneById.get(siteId)) || zone.timeZone),
    [zone, zoneById]
  );

  return <SiteTimeContext.Provider value={value}>{children}</SiteTimeContext.Provider>;
}

export function SiteTimeProvider({ children, timeZoneOverride }: SiteTimeProviderProps) {
  return timeZoneOverride ? (
    <PinnedSiteTime timeZone={timeZoneOverride}>{children}</PinnedSiteTime>
  ) : (
    <ActiveSiteTime>{children}</ActiveSiteTime>
  );
}

/** For a component rendered outside the provider; `isFallback` is true because there is no site. */
export const browserSiteTime: SiteTimeValue = bindSiteTime({
  ...resolveZone(browserTimeZone()),
  isFallback: true,
});
